
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
  formatFractionLatexCore
} from "../../../../components/inputs/FractionInput";

function gcd(a, b) {
  while (b !== 0) [a, b] = [b, a % b];
  return Math.abs(a);
}

function formatMixedFractionLatex(value) {
  const sign = value < 0 ? "-" : "";
  const abs = Math.abs(value);
  const intPart = Math.floor(abs);
  const frac = abs - intPart;

  if (frac === 0) return `${sign}${intPart}`;

  // Szukamy ułamka skracalnego z dokładnością do 1/100
  const maxDen = 10; // np. 10 → pozwala na 1/2, 1/3, ..., 9/10
  for (let d = 1; d <= maxDen; d++) {
    const n = Math.round(frac * d);
    if (Math.abs(n / d - frac) < 0.01) {
      const g = gcd(n, d);
      const num = n / g;
      const den = d / g;

      if (intPart === 0) return `${sign}\\dfrac{${num}}{${den}}`;
      return `${sign}${intPart}\\dfrac{${num}}{${den}}`;
    }
  }

  // fallback jeśli nie znaleziono ładnej postaci
  return `${sign}${abs.toFixed(2)}`;
}

export function generateTask() {
  const getRandomInt = (min, max) =>
    Math.floor(Math.random() * (max - min + 1)) + min;

  let A, B, C, a, b;
  do {
    A = getRandomInt(-5, 5);
  } while (A === 0); // A musi być różne od zera, bo dzielimy przez A

  do {
  B = getRandomInt(-5, 5);
} while (B === 0);

  C = getRandomInt(-5, 5);

  a = getRandomInt(-5, 5);
  while (a === B) {
    a = getRandomInt(-5, 5); // unikamy przypadków z m = 0
  }

  b = getRandomInt(-5, 5);

  // rozwiązanie
  const m = (a - B) / A;

  return {
    type: "FindParameterParallelTask",
    data: { A, B, C, a, b, m },
    answer: m
  };
}

export function renderTask({ data }) {
  const { A, B, C, a, b } = data;

  const firstA = A === 1 ? "m" :
                 A === -1 ? "-m" :
                 `${A}m`;

  const firstExpr = B === 0 ? firstA :
    B > 0 ? `${firstA} + ${B}` :
    `${firstA} - ${Math.abs(B)}`;

  const cPart = C === 0 ? "" :
    C > 0 ? ` + ${C}` :
    ` - ${Math.abs(C)}`;

  // 🧠 NOWY fragment — poprawne wyświetlanie prostej porównawczej:
  let secondExpr;

  if (a === 0) {
    secondExpr = `${b}`; // y = b
  } else {
    const aExpr =
      a === 1 ? "x" :
      a === -1 ? "-x" :
      `${a}x`;

    secondExpr = b === 0 ? `${aExpr}` :
      b > 0 ? `${aExpr} + ${b}` :
      `${aExpr} - ${Math.abs(b)}`;
  }

  return (
    <div className="space-y-2">
      <p>
        Dana jest prosta <InlineMath math={`y = (${firstExpr})x${cPart}`} /> oraz prosta{" "}
        <InlineMath math={`y = ${secondExpr}`} />.
      </p>
      <p>
        Dla jakiej wartości parametru <InlineMath math="m" /> proste są równoległe?
      </p>
    </div>
  );
}

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div className="flex items-center gap-4">
      <span><InlineMath math="m = " /></span>
      <FractionInput
        value={userAnswer || ""}
        onChange={onChange}
        disabled={disabled}
        label="m"
      />
    </div>
  );
}
export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;

  const parsed = userAnswer?.parsed;
  if (parsed == null || isNaN(parsed)) return false;

  const expected = data.m;
  return Math.abs(parsed - expected) < 0.01;
}

export function renderAnswer({ data }) {
  const mLatex = formatMixedFractionLatex(data.m);

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={`m = ${mLatex}`} />
    </div>
  );
}

export function renderExplanation({ data }) {
  const { A, B, a, m } = data;

  const numerator = a - B;
  const mLatex = formatMixedFractionLatex(m);

  const bSign = B >= 0 ? `+ ${B}` : `- ${Math.abs(B)}`;
  const leftExpr = `${A}m ${bSign}`; // bez nawiasów

  return (
    <div className="space-y-4" style={{ textAlign: "left" }}>
      <div>
        <strong>Krok 1:</strong> Proste są równoległe, jeśli ich współczynniki kierunkowe są równe, czyli:
        <BlockMath math={String.raw`a_{1}=a_{2}`} />
        <BlockMath math={`${leftExpr} = ${a}`} />
      </div>

      <div>
        <strong>Krok 2:</strong> Rozwiązujemy równanie względem <InlineMath math="m" />:
        <BlockMath math={`${leftExpr} = ${a}`} />
        <BlockMath math={`${A}m = ${numerator}`} />
        <BlockMath math={`m = \\dfrac{${numerator}}{${A}} = ${mLatex}`} />
      </div>
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }

  const latex = formatLatexPreview(userAnswer.raw, "m");

  return (
    <div style={{ marginTop: "1rem" }}>
      <InlineMath math={latex} />
    </div>
  );
}
